/* ── Country SEO Landing Page Data ──────────────────────────────────── */

import type { LucideIcon } from 'lucide-react';
import {
  Building2, CalendarCheck, CreditCard, BarChart3,
  Globe, Shield, Users, Wifi, BedDouble, Clock,
  FileCheck2, Scale, Landmark,
} from 'lucide-react';

export interface CountryFeature {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface CountryStat {
  value: string;
  label: string;
}

export interface CountryTestimonial {
  quote: string;
  name: string;
  role: string;
  hotel: string;
  initials: string;
}

export interface CountryCompliance {
  name: string;
  description: string;
}

export interface CountryFAQ {
  question: string;
  answer: string;
}

export interface CountryLandingConfig {
  slug: string;
  countryName: string;
  countryCode: string;
  heroTitle: string;
  heroSubtitle: string;
  industryStats: CountryStat[];
  features: CountryFeature[];
  complianceTitle: string;
  complianceItems: CountryCompliance[];
  testimonial: CountryTestimonial;
  faqItems: CountryFAQ[];
  ctaTitle: string;
  ctaSubtitle: string;
}

export const COUNTRY_CONFIGS: Record<string, CountryLandingConfig> = {
  us: {
    slug: 'hotel-management-software-us',
    countryName: 'United States',
    countryCode: 'US',
    heroTitle: 'Hotel Management Software for the United States',
    heroSubtitle:
      'Enterprise PMS built for American hotels, resorts, and hospitality groups. Manage multi-property operations with built-in US tax compliance, PCI DSS payment processing, and ADA-ready digital experiences.',
    industryStats: [
      { value: '55,000+', label: 'Hotels nationwide' },
      { value: '$260B', label: 'Annual industry revenue' },
      { value: '66.1%', label: 'Average occupancy rate' },
      { value: '$152', label: 'Average daily rate (ADR)' },
    ],
    features: [
      { icon: CreditCard, title: 'US Tax Engine', description: 'Automated transient occupancy tax (TOT), state sales tax, and tourism improvement district fees across all 50 states.' },
      { icon: Shield, title: 'PCI DSS Compliant Payments', description: 'Level-1 PCI DSS certified payment processing with tokenization and end-to-end encryption.' },
      { icon: Globe, title: 'OTA Channel Manager', description: 'Real-time sync with Booking.com, Expedia, Airbnb, VRBO, and 200+ distribution channels.' },
      { icon: Users, title: 'ADA-Ready Digital Experience', description: 'WCAG 2.1 AA compliant booking engine and guest portal for ADA accessibility.' },
      { icon: BarChart3, title: 'STR Benchmarking Integration', description: 'Automatic RevPAR, ADR, and occupancy benchmarking against your competitive set.' },
      { icon: Building2, title: 'Multi-Property Portfolio', description: 'Centralized dashboard for hotel groups with consolidated P&L, rate management, and staff scheduling.' },
    ],
    complianceTitle: 'Built-In US Regulatory Compliance',
    complianceItems: [
      { name: 'State & Local Tax', description: 'Automated TOT, sales tax, and TID fee calculation for all jurisdictions' },
      { name: 'PCI DSS Level 1', description: 'Certified payment card security with tokenization and quarterly scans' },
      { name: 'ADA Compliance', description: 'Digital accessibility standards for booking and guest services' },
      { name: 'CCPA / State Privacy', description: 'California Consumer Privacy Act and state-level data protection' },
      { name: 'OSHA Workplace Safety', description: 'Hospitality worker safety tracking and incident reporting' },
      { name: 'Form 1099-K Reporting', description: 'Automated OTA revenue reporting for IRS compliance' },
    ],
    testimonial: {
      quote: 'HotelStack transformed our 12-property portfolio across Florida and California. The automated TOT filing alone saves us 40 hours per month. The multi-property dashboard gives us real-time visibility we never had before.',
      name: 'David Harrington',
      role: 'VP of Operations',
      hotel: 'Coastal Hospitality Group, Miami',
      initials: 'DH',
    },
    faqItems: [
      { question: 'Does HotelStack handle US state and local hotel taxes?', answer: 'Yes. HotelStack automatically calculates transient occupancy tax (TOT), state sales tax, and tourism improvement district fees for all 50 states and major municipalities. Tax rates update automatically.' },
      { question: 'Is HotelStack PCI DSS compliant?', answer: 'HotelStack is Level-1 PCI DSS certified. All payment data is tokenized and encrypted end-to-end. We undergo quarterly ASV scans and annual on-site audits.' },
      { question: 'Can I manage multiple US properties from one dashboard?', answer: 'Absolutely. Our multi-property module provides consolidated reporting, centralized rate management, cross-property guest profiles, and portfolio-level analytics for hotel groups of any size.' },
      { question: 'Does HotelStack integrate with US OTAs?', answer: 'Yes. We connect with Booking.com, Expedia, Airbnb, VRBO, Hotels.com, and 200+ channels. Rates and availability sync in real-time to prevent overbooking.' },
    ],
    ctaTitle: 'Ready to Modernize Your US Hotel Operations?',
    ctaSubtitle: 'Join hundreds of American hotels using HotelStack to streamline operations, maximize revenue, and deliver exceptional guest experiences.',
  },
  uk: {
    slug: 'hotel-management-software-uk',
    countryName: 'United Kingdom',
    countryCode: 'UK',
    heroTitle: 'Hotel Management Software for the United Kingdom',
    heroSubtitle:
      'Enterprise PMS designed for UK hotels, boutique properties, and hospitality chains. HMRC-compliant VAT automation, Making Tax Digital ready, and GDPR-first data handling.',
    industryStats: [
      { value: '45,000+', label: 'Hotels & B&Bs' },
      { value: 'GBP 60B', label: 'Annual industry revenue' },
      { value: '73.2%', label: 'Average occupancy rate' },
      { value: 'GBP 115', label: 'Average daily rate (ADR)' },
    ],
    features: [
      { icon: Landmark, title: 'HMRC VAT Automation', description: 'Making Tax Digital (MTD) compliant VAT filing at 20% with automatic return generation and submission.' },
      { icon: Shield, title: 'GDPR-First Data Handling', description: 'Guest data processing with consent management, right-to-erasure workflows, and DPO tools.' },
      { icon: Globe, title: 'UK OTA Distribution', description: 'Real-time connectivity with Booking.com, Expedia, LateRooms, and Airbnb for UK market coverage.' },
      { icon: BedDouble, title: 'AA/Visit England Integration', description: 'Quality assessment tracking aligned with AA star ratings and Visit England accreditation.' },
      { icon: BarChart3, title: 'Revenue Management', description: 'Dynamic pricing engine with demand forecasting optimized for UK seasonal patterns and events.' },
      { icon: Users, title: 'Staff Scheduling (UK Labour)', description: 'Rota management compliant with UK Working Time Regulations and National Living Wage.' },
    ],
    complianceTitle: 'UK Regulatory Compliance Built In',
    complianceItems: [
      { name: 'HMRC VAT / MTD', description: 'Making Tax Digital compliant VAT returns with digital record keeping' },
      { name: 'GDPR', description: 'Full General Data Protection Regulation compliance for guest data' },
      { name: 'Fire Safety Order', description: 'Regulatory Reform (Fire Safety) Order 2005 tracking and documentation' },
      { name: 'Food Hygiene (FSA)', description: 'Food Standards Agency rating compliance and HACCP documentation' },
      { name: 'Equality Act 2010', description: 'Accessibility requirements for disabled guests and staff' },
      { name: 'Right to Work Checks', description: 'Immigration Act compliance for employee verification' },
    ],
    testimonial: {
      quote: 'We switched to HotelStack for our boutique hotel group in the Cotswolds. The MTD-compliant VAT module and GDPR tools were exactly what we needed. Guest satisfaction scores are up 18% since launch.',
      name: 'Emma Richardson',
      role: 'Managing Director',
      hotel: 'Cotswold Heritage Hotels, Oxfordshire',
      initials: 'ER',
    },
    faqItems: [
      { question: 'Is HotelStack Making Tax Digital (MTD) compliant?', answer: 'Yes. HotelStack generates MTD-compliant VAT returns with digital record keeping. You can file directly to HMRC through our integrated submission gateway.' },
      { question: 'How does HotelStack handle GDPR for UK hotels?', answer: 'HotelStack includes consent management, automated data retention policies, right-to-erasure workflows, breach notification tracking, and DPO dashboard tools built into every guest interaction.' },
      { question: 'Does HotelStack support UK-specific OTA channels?', answer: 'Yes. We integrate with all major UK channels including Booking.com, Expedia, Airbnb, LateRooms, and Trivago, plus GDS connections via Amadeus and Sabre.' },
      { question: 'Can HotelStack manage seasonal pricing for UK tourism?', answer: 'Our revenue management engine supports UK seasonal demand patterns, bank holiday pricing, event-based surcharges, and competitor rate monitoring.' },
    ],
    ctaTitle: 'Ready to Transform Your UK Hotel Operations?',
    ctaSubtitle: 'Join leading UK hotels using HotelStack to automate compliance, boost direct bookings, and deliver outstanding guest experiences.',
  },
  uae: {
    slug: 'hotel-management-software-uae',
    countryName: 'United Arab Emirates',
    countryCode: 'AE',
    heroTitle: 'Hotel Management Software for the UAE',
    heroSubtitle:
      'Enterprise PMS built for UAE luxury hotels, resorts, and hospitality groups. DTCM-compliant operations, FTA VAT automation, and multi-language guest experiences for the world\'s premier hospitality market.',
    industryStats: [
      { value: '1,200+', label: 'Hotels across 7 Emirates' },
      { value: '$12.5B', label: 'Annual tourism revenue' },
      { value: '76.8%', label: 'Average occupancy rate' },
      { value: '$185', label: 'Average daily rate (ADR)' },
    ],
    features: [
      { icon: CreditCard, title: 'FTA VAT Automation', description: 'Automated 5% VAT calculation, filing, and FTA Tax Return submission for accommodation and F&B.' },
      { icon: Landmark, title: 'DTCM Compliance', description: 'Tourism Dirham fee collection, classification reporting, and DTCM license management.' },
      { icon: Globe, title: 'Arabic + English Interface', description: 'Full bilingual RTL/LTR interface with Arabic guest communications and invoicing.' },
      { icon: Shield, title: 'e-Channels Integration', description: 'Automated guest data submission to police e-Channels system for immigration compliance.' },
      { icon: Users, title: 'WPS Payroll Integration', description: 'Staff salary processing compatible with UAE Wage Protection System requirements.' },
      { icon: BarChart3, title: 'Luxury Revenue Optimization', description: 'AI-driven dynamic pricing for premium segments with group and MICE rate management.' },
    ],
    complianceTitle: 'UAE Regulatory Compliance Automated',
    complianceItems: [
      { name: 'FTA VAT Filing', description: 'Automated 5% VAT calculation and Federal Tax Authority return submission' },
      { name: 'Tourism Dirham', description: 'Per-night tourism fee collection and DTCM remittance' },
      { name: 'Municipality Fee', description: 'Emirate-specific municipality fee on room revenue' },
      { name: 'e-Channels Guest Reporting', description: 'Automated guest data submission to police systems' },
      { name: 'DTCM Hotel License', description: 'Classification and operating license compliance tracking' },
      { name: 'PDPL Data Protection', description: 'UAE Personal Data Protection Law compliance for guest records' },
    ],
    testimonial: {
      quote: 'HotelStack manages our 5-star resort operations seamlessly. The DTCM integration and Tourism Dirham automation eliminated manual reporting entirely. Our team in Dubai processes 300+ check-ins daily without friction.',
      name: 'Ahmed Al-Mansouri',
      role: 'General Manager',
      hotel: 'Palm Residences Resort & Spa, Dubai',
      initials: 'AA',
    },
    faqItems: [
      { question: 'Does HotelStack handle UAE VAT and Tourism Dirham fees?', answer: 'Yes. HotelStack automatically calculates 5% VAT on all taxable services and collects Tourism Dirham fees (AED 7-20 per room night based on hotel classification). Both are filed to FTA and DTCM respectively.' },
      { question: 'Can HotelStack submit guest data to UAE police e-Channels?', answer: 'HotelStack integrates with the police e-Channels system for automated guest registration. Foreign and domestic guest data is submitted within the mandatory timeframe.' },
      { question: 'Does HotelStack support Arabic language?', answer: 'Yes. HotelStack provides a full bilingual interface with RTL Arabic support for staff, guest-facing communications, invoices, and reports.' },
      { question: 'Is HotelStack suitable for luxury UAE hotels?', answer: 'Absolutely. HotelStack serves 5-star hotels and resorts with advanced features including MICE management, spa and recreation integration, and premium guest profile management.' },
    ],
    ctaTitle: 'Elevate Your UAE Hotel Operations',
    ctaSubtitle: 'Join premium UAE hotels using HotelStack to automate compliance, optimize revenue, and deliver world-class guest experiences.',
  },
  thailand: {
    slug: 'hotel-management-software-thailand',
    countryName: 'Thailand',
    countryCode: 'TH',
    heroTitle: 'Hotel Management Software for Thailand',
    heroSubtitle:
      'Enterprise PMS designed for Thai hotels, resorts, and hospitality businesses. TM.30 immigration compliance, Thai Revenue Department integration, and multi-language support for the Land of Smiles.',
    industryStats: [
      { value: '32,000+', label: 'Hotels & resorts' },
      { value: '$65B', label: 'Tourism GDP contribution' },
      { value: '71.5%', label: 'Average occupancy rate' },
      { value: 'THB 3,200', label: 'Average daily rate (ADR)' },
    ],
    features: [
      { icon: FileCheck2, title: 'TM.30 Auto-Submission', description: 'Automated TM.30 foreigner notification to Immigration Bureau within 24 hours of check-in.' },
      { icon: CreditCard, title: 'Thai VAT Engine', description: '7% VAT calculation with withholding tax (WHT) on contractor payments and Revenue Department filing.' },
      { icon: Globe, title: 'Thai + English + Chinese', description: 'Trilingual interface supporting Thai, English, and Simplified Chinese for staff and guests.' },
      { icon: Building2, title: 'Hotel Act (Ror Ror 1)', description: 'License tracking and compliance documentation for Thai Hotel Act requirements.' },
      { icon: Clock, title: 'Tourism Authority Integration', description: 'TAT registration, SHA Plus certification tracking, and tourism statistics reporting.' },
      { icon: BarChart3, title: 'Seasonal Revenue Management', description: 'Dynamic pricing optimized for Thai high/low seasons with festival and event-based surcharges.' },
    ],
    complianceTitle: 'Thai Regulatory Compliance Automated',
    complianceItems: [
      { name: 'TM.30 Immigration', description: 'Automated foreigner notification within 24 hours of arrival' },
      { name: 'Revenue Department VAT', description: '7% VAT filing and withholding tax compliance' },
      { name: 'Hotel Act License', description: 'Ror Ror 1 hotel operating license compliance tracking' },
      { name: 'PDPA Data Protection', description: 'Thailand Personal Data Protection Act guest data compliance' },
      { name: 'Excise License', description: 'Liquor and entertainment license management (Por Sor 1)' },
      { name: 'FDA Food License', description: 'Sor Bor 1 food establishment license for hotel restaurants' },
    ],
    testimonial: {
      quote: 'The TM.30 automation alone justified our switch to HotelStack. We process 200+ foreign guest check-ins weekly in Phuket, and immigration compliance is now fully automated. Revenue is up 22% with the dynamic pricing engine.',
      name: 'Somchai Prasert',
      role: 'Director of Operations',
      hotel: 'Andaman Bay Resorts, Phuket',
      initials: 'SP',
    },
    faqItems: [
      { question: 'Does HotelStack automate TM.30 foreigner notification?', answer: 'Yes. HotelStack automatically generates and submits TM.30 notifications to the Immigration Bureau within 24 hours of foreign guest check-in, fully complying with Thai immigration law.' },
      { question: 'Does HotelStack handle Thai VAT and withholding tax?', answer: 'HotelStack calculates 7% VAT on accommodation and F&B services, manages withholding tax on contractor payments, and generates Revenue Department-ready tax returns.' },
      { question: 'Is HotelStack available in Thai language?', answer: 'Yes. HotelStack supports Thai, English, and Chinese interfaces for staff operations, guest communications, invoices, and reports.' },
      { question: 'Can HotelStack manage resort operations in Thailand?', answer: 'Absolutely. HotelStack supports spa management, pool/beach services, excursion booking, and multi-outlet F&B operations common in Thai resort properties.' },
    ],
    ctaTitle: 'Modernize Your Thai Hotel Operations',
    ctaSubtitle: 'Join leading Thai hotels and resorts using HotelStack to automate compliance, boost revenue, and create memorable guest experiences.',
  },
  australia: {
    slug: 'hotel-management-software-australia',
    countryName: 'Australia',
    countryCode: 'AU',
    heroTitle: 'Hotel Management Software for Australia',
    heroSubtitle:
      'Enterprise PMS built for Australian hotels, resorts, and accommodation providers. ATO-compliant GST automation, BAS filing, and superannuation management for the Australian hospitality industry.',
    industryStats: [
      { value: '8,500+', label: 'Hotels & accommodation' },
      { value: 'AUD 50B', label: 'Annual tourism revenue' },
      { value: '68.4%', label: 'Average occupancy rate' },
      { value: 'AUD 195', label: 'Average daily rate (ADR)' },
    ],
    features: [
      { icon: CreditCard, title: 'ATO GST & BAS Filing', description: 'Automated 10% GST calculation with quarterly Business Activity Statement generation and ATO submission.' },
      { icon: Scale, title: 'Superannuation Management', description: 'Automated super guarantee contributions at 11.5% with SuperStream-compliant payments.' },
      { icon: Shield, title: 'Privacy Act Compliance', description: 'Australian Privacy Principles (APPs) built into guest data collection, storage, and processing.' },
      { icon: Globe, title: 'AU/NZ OTA Distribution', description: 'Connectivity with Booking.com, Expedia, Airbnb, Stayz, and Wotif for Australian market reach.' },
      { icon: Wifi, title: 'Remote Property Support', description: 'Low-bandwidth mode and offline capability for regional and outback accommodation providers.' },
      { icon: BarChart3, title: 'Tourism Australia Integration', description: 'Star rating alignment, quality accreditation tracking, and ABS tourism statistics reporting.' },
    ],
    complianceTitle: 'Australian Regulatory Compliance Built In',
    complianceItems: [
      { name: 'GST / BAS Filing', description: 'Automated 10% GST calculation and quarterly Business Activity Statements' },
      { name: 'PAYG Withholding', description: 'Employee tax withholding and ATO remittance automation' },
      { name: 'Superannuation Guarantee', description: '11.5% super contributions with SuperStream compliance' },
      { name: 'Privacy Act (APPs)', description: 'Australian Privacy Principles for guest data protection' },
      { name: 'WHS Compliance', description: 'Work Health and Safety Act tracking for hospitality staff' },
      { name: 'Liquor Licensing', description: 'State-specific liquor license tracking and renewal alerts' },
    ],
    testimonial: {
      quote: 'HotelStack handles our BAS lodgment and super contributions flawlessly. We run 8 properties from Sydney to Cairns, and the multi-property dashboard gives us instant visibility. Staff love the intuitive interface.',
      name: 'Sarah Mitchell',
      role: 'Chief Operating Officer',
      hotel: 'Pacific Coast Hotels, Sydney',
      initials: 'SM',
    },
    faqItems: [
      { question: 'Does HotelStack handle Australian GST and BAS filing?', answer: 'Yes. HotelStack automatically calculates 10% GST on all taxable supplies, generates quarterly Business Activity Statements, and supports direct ATO submission through integrated lodgment.' },
      { question: 'Can HotelStack manage superannuation for hotel staff?', answer: 'HotelStack calculates and tracks super guarantee contributions at 11.5%, generates SuperStream-compliant payment files, and integrates with major super clearing houses.' },
      { question: 'Does HotelStack integrate with Australian OTAs?', answer: 'Yes. We connect with Booking.com, Expedia, Airbnb, Stayz, Wotif, and 200+ channels. Rates and availability sync in real-time across all platforms.' },
      { question: 'Is HotelStack suitable for regional Australian properties?', answer: 'Absolutely. HotelStack includes low-bandwidth mode and offline capability for regional and remote properties, with data sync when connectivity is restored.' },
    ],
    ctaTitle: 'Transform Your Australian Hotel Operations',
    ctaSubtitle: 'Join leading Australian hotels using HotelStack to automate compliance, optimize revenue, and deliver exceptional guest experiences across every property.',
  },
};

export const COUNTRY_FAQ: Record<string, CountryFAQ[]> = Object.fromEntries(
  Object.entries(COUNTRY_CONFIGS).map(([key, config]) => [
    config.slug,
    config.faqItems,
  ]),
);
